/**
 * Customer-dashboard demo for resellers: OUR real dashboard, running in a sealed tab (Martin, 2026-09-24:
 * "genau unser Dashboard"). Imported first in index.tsx so it runs before any module reads storage.
 *
 * In a demo tab (opened with ?wl_demo=1, remembered per tab in sessionStorage):
 *  - the tab gets its own in-memory localStorage, seeded with a demo customer. The reseller's real session
 *    in other tabs is never read by the app and never overwritten;
 *  - every API request is answered here with example data (fixtures.ts); nothing reaches the backend, and
 *    writes are refused with a "this is a demo" message;
 *  - our Whitelabel programme and every service or extra the reseller switched off leave the menu;
 *  - the sidebar shows the reseller's name instead of ours (brandConfig is patched in place).
 *  - our orange becomes the reseller's colour (recolor.ts re-hues every orange in the tab's stylesheets);
 *  - prices, package sizes, payment methods and coupons are the reseller's (applyShop below, fixtures.ts).
 */
import { brandConfig } from '../../../config/brand';
import { BULK_OPTIONS, PRICING_MATRIX, TIERS, VOLUME_STEPS } from '../../../constants/pricing';
import { tenantCheckout, type TenantMethod } from '../../../config/tenantCheckout';
import { VISIT_SIZES, catalog, isOffered, priceOf } from '../catalog';
import { INITIAL_SETUP, type SetupState } from '../setup/setupState';
import { loadSetup } from '../setup/setupStore';
import { DEMO_USER, handleDemoRequest } from './fixtures';
import { startRecolor } from './recolor';
import { demoTenant } from './tenant';

const FLAG = 'tc_wl_demo';

function wantsDemo(): boolean {
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get('wl_demo') === '0') { sessionStorage.removeItem(FLAG); return false; }
    if (params.get('wl_demo') === '1') sessionStorage.setItem(FLAG, '1');
    return sessionStorage.getItem(FLAG) === '1';
  } catch {
    return false;
  }
}

/** The reseller's saved setup, read from the real storage before the tab is sealed. */
function readSetup(): SetupState {
  try { return loadSetup()?.s ?? INITIAL_SETUP; } catch { return INITIAL_SETUP; }
}

/** Menu items no reseller customer sees: our Whitelabel programme, our link building, our invoices (v1), and
 * whatever the reseller switched off. Roadmap, rewards and affiliate are out of v1 for real shops. */
function hiddenItems(services: Record<string, boolean>, extras: Record<string, boolean>, shop: boolean): string[] {
  return ['reseller', 'link-building',
    ...(shop ? ['billing', 'roadmap', 'gamification', 'affiliate'] : []),
    ...(!services.gsc ? ['gsc-traffic'] : []), ...(!services.local ? ['local-seo-traffic'] : []),
    ...(!services.yt ? ['youtube-smm'] : []), ...(!extras.tickets ? ['support'] : []),
    ...(!extras.roadmap ? ['roadmap'] : []), ...(!extras.rewards ? ['gamification'] : []),
    ...(!extras.affiliate ? ['affiliate'] : [])];
}

/**
 * Puts the reseller's shop into our pricing tables, in place, before any page reads them: only the sizes
 * they sell, at their one-month price; month bundles keep our bundle ratio (12 months = 10 × one month), so
 * "2 months free" stays true. Expert leaves the quality picker when it is not sold. The server prices every
 * order with the same rule (backend/tenants/catalog.py visit_price).
 */
function installPrices(sizes: number[], oneMonth: (tier: 'professional' | 'expert', qty: number) => number | undefined, expertOn: boolean): void {
  for (const tier of ['professional', 'expert'] as const) {
    const ours = { ...PRICING_MATRIX[tier] };
    for (const k of Object.keys(PRICING_MATRIX[tier])) delete PRICING_MATRIX[tier][Number(k)];
    for (const qty of sizes) {
      const one = oneMonth(tier, qty);
      if (one === undefined || !ours[qty]) continue;
      PRICING_MATRIX[tier][qty] = Object.fromEntries(BULK_OPTIONS.map((b) => [b, Math.round(one * (ours[qty][b] / ours[qty][1]) * 100) / 100]));
    }
  }
  (VOLUME_STEPS as unknown as number[]).splice(0, VOLUME_STEPS.length, ...sizes);
  const expertAt = TIERS.findIndex((t) => t.id === 'expert');
  if (!expertOn && expertAt >= 0) TIERS.splice(expertAt, 1);
}

/** The demo: prices and methods from the setup saved in this browser. */
function applyShop(s: SetupState): void {
  const sizes = VISIT_SIZES.filter((v) => s.offered.includes(String(v)));
  // The slider needs two ends; with fewer sizes on sale the next of ours is added at the markup price.
  while (sizes.length < 2) sizes.push(VISIT_SIZES.find((v) => !sizes.includes(v))!);
  sizes.sort((a, b) => a - b);
  installPrices(sizes, (tier, qty) => { const i = catalog(tier).find((x) => x.qty === qty); return i ? priceOf(s, i) : undefined; },
    s.expert && catalog('expert').some((i) => isOffered(s, i)));

  const methods = (Object.keys(s.pay) as Array<keyof SetupState['pay']>).filter((k) => s.pay[k]) as TenantMethod[];
  if (s.customPay.on && s.customPay.name.trim()) methods.push('custom');
  tenantCheckout.methods = methods;
  // Transfer details are never stored in the browser (setupStore.ts), so the demo shows where they go.
  const holder = s.business.company.trim() || s.name.trim() || 'Your company';
  tenantCheckout.bank = { holder, iban: 'Your IBAN', bic: 'Your BIC', bankName: 'Your bank', note: 'Your own bank details from your admin appear here.' };
  tenantCheckout.crypto = { name: 'Tether (USDT)', network: 'Tron (TRC20)', address: 'Your wallet address from your admin', note: 'Your own wallet appears here.' };
  tenantCheckout.custom = { name: s.customPay.name.trim(), how: s.customPay.how.trim() };
}

/** A real reseller host: everything comes from the server (backend/tenants/config.py public_view), injected
 * into the page as window.__TC_TENANT__ before any script runs. */
export interface ShopView {
  id: string; status: string; host: string; name: string; logo: string | null; color: string; mode: string;
  services: Record<string, boolean>; extras: Record<string, boolean>; expert: boolean;
  catalog: Record<string, Array<{ qty: number; bonus: number; price: number }>>;
  sizes: Record<string, number[]>; sizePrices: Record<string, Record<string, number>>;
  methods: TenantMethod[]; customPay: { name: string; how: string } | null;
  bank: { bankHolder: string; iban: string; bic: string; bankName: string } | null;
  crypto: { wallet: string; network: string; asset: string } | null;
  supportEmail: string; company: string; legal: { termsUrl?: string; privacyUrl?: string };
}

function applyShopView(v: ShopView): void {
  const sizes = v.sizes.professional ?? [];
  if (sizes.length) installPrices(sizes, (tier, qty) => v.sizePrices[tier]?.[String(qty)], !!v.expert && !!v.sizePrices.expert);
  tenantCheckout.methods = v.methods;
  // Transfer details are not in the page any more; they load after sign-in (useTenantPayDetails).
  tenantCheckout.custom = v.customPay ?? undefined;
  tenantCheckout.live = true;
}

/** Brand name, logo letter, colour and support address instead of ours, everywhere the dashboard reads them. */
function applyBrand(brand: { name: string; color: string; email: string; hidden: string[] }, shop: boolean): void {
  Object.assign(brandConfig as unknown as Record<string, unknown>, {
    id: shop ? 'reseller-shop' : 'reseller-demo', name: brand.name, shortName: brand.name, logoLetter: monogram(brand.name),
    supportWhatsAppNumber: undefined, supportEmail: brand.email || 'support@yourbrand.example',
    primaryColor: brand.color, primaryHover: brand.color,
  });
  startRecolor(brand.color);
  // The static first-paint loader in index.html names us; rename it before React replaces it.
  const loaderText = document.getElementById('loader-brand-text');
  if (loaderText) loaderText.textContent = brand.name;
  const loaderMark = document.querySelector('#loader-icon-box span');
  if (loaderMark) loaderMark.textContent = monogram(brand.name);
  // Some page titles carry our name as fixed text; the tab title is the first thing a customer reads.
  const fixTitle = () => {
    if (/Website Traffic Generator/.test(document.title)) document.title = brand.name; // index.html's own title
    else if (/Traffic Creator/.test(document.title)) document.title = document.title.replace(/Traffic Creator/g, brand.name);
  };
  fixTitle();
  new MutationObserver(fixTitle).observe(document.head, { subtree: true, childList: true, characterData: true });
  (window as unknown as Record<string, unknown>).__TC_WL_DEMO__ = { hidden: brand.hidden, shop };
  document.documentElement.dataset.wlDemo = '1';
}

function memoryStorage(seed: Record<string, string>): Storage {
  const data = new Map(Object.entries(seed));
  return {
    get length() { return data.size; },
    clear: () => data.clear(),
    getItem: (k) => (data.has(k) ? data.get(k)! : null),
    key: (i) => Array.from(data.keys())[i] ?? null,
    removeItem: (k) => { data.delete(k); },
    setItem: (k, v) => { data.set(k, String(v)); },
  };
}

const monogram = (name: string) => name.trim().split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase() || 'Y';

function apiOrigin(): string {
  try { return new URL(brandConfig.apiUrl).origin; } catch { return window.location.origin; }
}

/** Requests the demo answers: everything to a separate API origin, or non-file paths on our own origin. */
function isApiRequest(url: URL): boolean {
  if (url.origin === apiOrigin() && url.origin !== window.location.origin) return true;
  if (url.origin !== window.location.origin) return false;
  if (/\.[a-z0-9]{2,5}$/i.test(url.pathname)) return false; // release.json, assets, fonts, modules
  return !/^\/(@vite|@id|@fs|node_modules|src|assets|fonts|brand|img)\b/.test(url.pathname);
}

function installFetch(): void {
  const realFetch = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const req = input instanceof Request ? input : null;
    const url = new URL(req ? req.url : String(input), window.location.href);
    if (!isApiRequest(url)) return realFetch(input as RequestInfo, init);
    const method = (init?.method || req?.method || 'GET').toUpperCase();
    const { status, body } = handleDemoRequest(method, url.pathname, url.searchParams);
    return new Response(body === undefined ? null : JSON.stringify(body), {
      status,
      headers: { 'Content-Type': 'application/json' },
    });
  };
}

function showRibbon(name: string): void {
  const mount = () => {
    const el = document.createElement('div');
    el.setAttribute('role', 'note');
    el.style.cssText = 'position:fixed;left:50%;bottom:16px;translate:-50% 0;z-index:2147483000;display:flex;align-items:center;gap:12px;padding:9px 12px 9px 16px;border-radius:999px;background:#080a0f;color:#fff;font:600 13px/1.2 Inter,system-ui,sans-serif;box-shadow:0 16px 40px -12px rgb(8 10 15/.5)';
    const text = document.createElement('span');
    text.textContent = `Demo of ${name}'s customer dashboard · example data · nothing is saved`;
    const close = document.createElement('button');
    close.type = 'button';
    close.textContent = 'Close demo';
    close.style.cssText = 'min-height:32px;padding:0 12px;border:0;border-radius:999px;background:#fff;color:#080a0f;font:800 12px Inter,system-ui,sans-serif;cursor:pointer';
    close.onclick = () => { try { sessionStorage.removeItem(FLAG); } catch { /* */ } window.close(); window.location.href = '/dashboard?wl_demo=0'; };
    el.append(text, close);
    document.body.appendChild(el);
  };
  if (document.body) mount(); else window.addEventListener('DOMContentLoaded', mount, { once: true });
}

export const isResellerDemo = wantsDemo();

const shopView = (window as unknown as { __TC_TENANT__?: ShopView }).__TC_TENANT__;
/** True on a reseller's own host (real shop), false in our demo tab. */
export const isResellerShop = !!shopView;

if (shopView) {
  applyShopView(shopView);
  applyBrand({ name: shopView.name, color: shopView.color, email: shopView.supportEmail, hidden: hiddenItems(shopView.services, shopView.extras, true) }, true);
} else if (isResellerDemo) {
  const s = readSetup();
  demoTenant.s = s;
  const storage = memoryStorage({
    tgp_token: 'demo-session',
    modus_current_user: JSON.stringify(DEMO_USER),
    modus_current_user_id: DEMO_USER.id,
    reseller_banner_dismissed: 'true',
  });
  Object.defineProperty(window, 'localStorage', { value: storage, configurable: true });
  installFetch();
  applyShop(s);
  const name = s.name.trim() || 'Your brand';
  applyBrand({ name, color: s.color || '#2563eb', email: s.business.email.trim(), hidden: hiddenItems(s.services, s.extras, false) }, false);
  showRibbon(name);
}
